import { Controller } from '@hotwired/stimulus'

const FIELD_IDS = {
  title: 'product_name',
  brand: 'product_brand',
  variant_label: 'product_variant_label',
  model_number: 'product_model_number',
  tags: 'product_product_tags',
  meta_description: 'product_meta_description',
  description: 'product_description'
}

export default class extends Controller {
  static targets = ['button', 'status', 'result']
  static values = {
    url: String,
    csrfToken: String
  }

  async generate () {
    this.setLoading(true)
    this.setStatus('Analyzing your listing...')
    this.hideResult()

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({ product: this.productPayload() })
      })
      const payload = await response.json()
      if (!response.ok || !payload.ok) throw new Error(payload.message || 'Growth kit analysis failed.')

      this.renderResult(payload.suggestions, payload.usage)
      this.setStatus('')
    } catch (error) {
      this.setStatus(error.message || 'Could not analyze listing. Try again.')
    } finally {
      this.setLoading(false)
    }
  }

  applyAll (event) {
    event.preventDefault()
    const suggestions = this.currentSuggestions
    if (!suggestions) return

    this.applyTitle(suggestions.title)
    this.applyDescription(suggestions.description)
    this.applyTags(this.mergedTags(suggestions))
    this.applyMeta(suggestions.meta_description)
    this.setStatus('Suggestions applied. SEO keywords added to Extra tags. Review before saving.')
  }

  mergedTags (suggestions) {
    const suggested = Array.isArray(suggestions.tags) ? suggestions.tags : []
    const keywords = Array.isArray(suggestions.seo_keywords) ? suggestions.seo_keywords : []
    const existing = this.valueOf(FIELD_IDS.tags)
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)

    const seen = new Set()
    const merged = []
    ;[...existing, ...suggested, ...keywords].forEach((tag) => {
      const key = tag.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      merged.push(tag)
    })
    return merged
  }

  productPayload () {
    const categorySelect = document.getElementById('product_category_id') || document.getElementById('medicine_form_category')
    const categoryName = categorySelect?.selectedOptions?.[0]?.textContent?.replace('· medicines', '').trim()
    const imageInput = document.querySelector('[data-image-preview-target="input"]')

    return {
      name: this.valueOf('product_name') || this.valueOf('[name="product[name]"]'),
      brand: this.valueOf('product_brand'),
      variant_label: this.valueOf('product_variant_label'),
      model_number: this.valueOf('product_model_number'),
      condition: this.valueOf('[name="product[condition]"]'),
      category_id: categorySelect?.value,
      category_name: categoryName,
      sale_price: this.valueOf('product_sale_price'),
      color: this.valueOf('product_color'),
      size: this.valueOf('product_size'),
      material: this.valueOf('product_material'),
      storage_gb: this.valueOf('product_storage_gb'),
      ram_gb: this.valueOf('product_ram_gb'),
      battery_health: this.valueOf('product_battery_health'),
      network_type: this.valueOf('product_network_type'),
      generic_name: this.valueOf('product_generic_name'),
      manufacturer: this.valueOf('product_manufacturer'),
      dosage_form: this.valueOf('product_dosage_form'),
      strength: this.valueOf('product_strength'),
      pack_size: this.valueOf('product_pack_size'),
      product_tags: this.valueOf('product_product_tags'),
      description: this.valueOf('product_description') || this.richTextValue(),
      has_images: Boolean(imageInput?.files?.length)
    }
  }

  renderResult (suggestions, usage) {
    this.currentSuggestions = suggestions
    if (!this.hasResultTarget) return

    const tags = Array.isArray(suggestions.tags) ? suggestions.tags : []
    const tips = Array.isArray(suggestions.photo_tips) ? suggestions.photo_tips : []
    const fixes = Array.isArray(suggestions.priority_fixes) ? suggestions.priority_fixes : []
    const keywords = Array.isArray(suggestions.seo_keywords) ? suggestions.seo_keywords : []
    const score = Number(suggestions.quality_score || 0)
    const scoreClass = score >= 80 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : score >= 55 ? 'text-amber-800 bg-amber-50 border-amber-200' : 'text-rose-800 bg-rose-50 border-rose-200'
    const fallback = suggestions.fallback ? '<p class="mt-2 text-xs font-semibold text-amber-700">Local fallback used because AI provider is not configured.</p>' : ''
    const usageText = usage?.remaining_today !== undefined ? `<p class="mt-2 text-xs text-slate-500">AI calls left today: ${usage.remaining_today}</p>` : ''

    this.resultTarget.innerHTML = `
      <div class="space-y-5">
        <div class="rounded-2xl border px-4 py-4 ${scoreClass}">
          <p class="text-[11px] font-bold uppercase tracking-wide">Listing quality score</p>
          <p class="mt-1 text-3xl font-black">${score}<span class="text-base font-bold">/100</span></p>
          <p class="mt-1 text-sm">Higher score means buyers can trust and find your listing faster.</p>
        </div>

        <div>
          <p class="text-[11px] font-bold uppercase tracking-wide text-slate-500">Priority fixes</p>
          <ul class="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">${fixes.map((fix) => `<li>${this.escape(fix)}</li>`).join('')}</ul>
        </div>

        <div>
          <p class="text-[11px] font-bold uppercase tracking-wide text-slate-500">SEO keywords</p>
          <p class="mt-1 text-sm text-slate-700">${this.escape(keywords.join(', '))}</p>
          <p class="mt-1 text-xs text-slate-500">Apply all will merge these into Extra tags for marketplace search.</p>
        </div>

        <div>
          <p class="text-[11px] font-bold uppercase tracking-wide text-slate-500">Suggested title</p>
          <p class="mt-1 text-sm font-bold text-slate-900">${this.escape(suggestions.title || '')}</p>
        </div>

        <div>
          <p class="text-[11px] font-bold uppercase tracking-wide text-slate-500">Description</p>
          <div class="mt-1 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">${suggestions.description || ''}</div>
        </div>

        <div>
          <p class="text-[11px] font-bold uppercase tracking-wide text-slate-500">Tags</p>
          <p class="mt-1 text-sm text-slate-700">${this.escape(tags.join(', '))}</p>
        </div>

        <div>
          <p class="text-[11px] font-bold uppercase tracking-wide text-slate-500">Photo tips</p>
          <ul class="mt-1 list-inside list-disc text-sm text-slate-700">${tips.map((tip) => `<li>${this.escape(tip)}</li>`).join('')}</ul>
        </div>

        <button type="button" class="inline-flex rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800" data-action="click->ai-listing-assistant#applyAll">
          Apply all suggestions
        </button>
        ${fallback}
        ${usageText}
      </div>
    `
    this.resultTarget.classList.remove('hidden')
  }

  applyTitle (value) {
    this.setField('product_name', value)
  }

  applyTags (tags) {
    this.setField(FIELD_IDS.tags, Array.isArray(tags) ? tags.join(', ') : tags)
  }

  applyMeta (value) {
    this.setField(FIELD_IDS.meta_description, value)
  }

  applyDescription (html) {
    const input = document.getElementById(FIELD_IDS.description)
    if (!input) return

    input.value = html || ''
    input.dispatchEvent(new Event('input', { bubbles: true }))

    const editor = input.closest('.rich-editor-field')?.querySelector('.ql-editor')
    if (editor) editor.innerHTML = html || '<p><br></p>'
  }

  setField (id, value) {
    const field = document.getElementById(id)
    if (!field || value === undefined || value === null) return

    field.value = value
    field.dispatchEvent(new Event('input', { bubbles: true }))
    field.dispatchEvent(new Event('change', { bubbles: true }))
  }

  valueOf (idOrSelector) {
    const field = idOrSelector[0] === '['
      ? document.querySelector(idOrSelector)
      : document.getElementById(idOrSelector)
    return field?.value?.trim() || ''
  }

  richTextValue () {
    const editor = document.querySelector('.rich-editor-field .ql-editor')
    const text = editor?.textContent?.replace(/\s+/g, ' ').trim()
    return text && text !== '' ? text : ''
  }

  setLoading (loading) {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = loading
    this.buttonTarget.textContent = loading ? 'Analyzing...' : 'Analyze listing'
  }

  setStatus (message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle('hidden', !message)
  }

  hideResult () {
    if (!this.hasResultTarget) return
    this.resultTarget.classList.add('hidden')
    this.resultTarget.innerHTML = ''
  }

  escape (value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
